import { Row, Table } from 'antd';
import classNames from 'classnames';
import { camelCase } from 'lodash';
import groupBy from 'lodash/groupBy';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Field, FieldWithValue, FieldValue, FieldValueType } from 'api/tasks';
import { EffortDurationField } from 'components/organisms/task-pane/task-detail/task-fields/default-task-fields/effort-as-duration';
import { NumberField } from 'components/organisms/tasks/fields';
import { Text02 } from 'components/styles';
import { STATUS_FIELD_NAME } from 'data/fields/hooks';
import { EditableCell } from './editable-cell';
import { InputTextField } from './editable-cell/input-text-field';
import styles from './task-fields.module.css';
import { TableColumn } from './types';

const Left = styled.div`
  ${Text02};
  height: 40px;
  align-items: center;
  display: flex;
  padding: var(--spacing-03) var(--spacing-03) var(--spacing-03)
    var(--spacing-05);
  flex: 1;
`;
const Right = styled.div`
  display: flex;
  flex: 1;
`;

function columnGrouped(groupName: string): Array<TableColumn> {
  return [
    {
      className: classNames('text-02', styles.row),
      title: groupName,
      dataIndex: 'fieldLabel',
      key: 'fieldLabel',
      editable: false,
      width: '40%',
    },
    {
      className: `${styles.row} ${styles.row_value}`,
      title: '',
      editable: true,
      dataIndex: 'value',
      key: 'value',
    },
  ];
}

type FieldGroup = string;

type GroupField = Record<FieldGroup, Field[]>;

interface TaskFieldGroupProps {
  groupedFields: GroupField;
  onChange: (value, row) => void;
  externalRefKey?: string;
  effort?: number;
  onExternalRefkeyChange?: (value: string | null) => void;
  onEffortChange?: (value: number | null) => void;
  effortField?: NumberField;
}

function rowClassName(record: FieldWithValue): string {
  return record.private ? styles.private : '';
}

const TaskFieldGroup: FC<TaskFieldGroupProps> = (props) => {
  const {
    groupedFields,
    onChange,
    effort,
    externalRefKey,
    onEffortChange,
    effortField,
    onExternalRefkeyChange,
  } = props;
  const handleSave = (value, row) => {
    onChange(value, row);
  };
  const components = {
    body: {
      cell: EditableCell,
    },
  };
  const getColumns = (columns) =>
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave,
        }),
      };
    });

  return (
    <>
      {Object.keys(groupedFields).map((data) => (
        <div className={styles.wrapper} key={data}>
          <h5 className={styles.group__header}>
            {!data || data === 'undefined' ? (
              <FormattedMessage id="custom_fields" />
            ) : (
              data
            )}
          </h5>
          {data === 'Default' ? (
            <>
              {effortField ? (
                <Row>
                  <Left>
                    <FormattedMessage id="effort" />
                  </Left>
                  <Right data-cy="right.content.effort">
                    <EffortDurationField
                      onChange={(e) => onEffortChange?.(e)}
                      field={effortField}
                      value={effort}
                    />
                  </Right>
                </Row>
              ) : null}
              <Row>
                <Left>
                  <FormattedMessage id="external_ref_key" />
                </Left>
                <Right>
                  <InputTextField
                    onChange={(e) => onExternalRefkeyChange?.(e)}
                    initialValue={externalRefKey}
                  />
                </Right>
              </Row>
            </>
          ) : null}
          <Table
            className={styles.table}
            components={components}
            pagination={false}
            size="small"
            rowKey="fieldId"
            columns={getColumns(columnGrouped(data))}
            dataSource={groupedFields[data]}
            showHeader={false}
            rowClassName={rowClassName}
          />
        </div>
      ))}
    </>
  );
};

interface TaskFieldsProps {
  fields: Array<Field>;
  value: Array<FieldValue<FieldValueType>>;
  onChange: (value) => void;
  externalRefKey?: string;
  effort?: number;
  onExternalRefkeyChange?: (value: string | null) => void;
  onEffortChange?: (value: number | null) => void;
  effortField?: NumberField;
}

const AT_RISK_FIELD_NAME = 'atRisk';

const NATIVE_FIELDS_NAME = [STATUS_FIELD_NAME, AT_RISK_FIELD_NAME];

export const TaskFieldsList: FC<TaskFieldsProps> = (props) => {
  const {
    fields = [],
    value = [],
    onChange,
    externalRefKey,
    effort,
    onExternalRefkeyChange,
    onEffortChange,
    effortField,
  } = props;
  const taskFieldValueHash = value.reduce((obj, field) => {
    const o = { ...obj };

    o[field.fieldId] = field.fieldValue;

    return o;
  }, {});

  const filteredFields = fields.filter(
    (f) =>
      !NATIVE_FIELDS_NAME.map(camelCase).includes(camelCase(f.fieldName) || ''),
  );

  const fieldsWithValue = filteredFields.map(
    (field): FieldWithValue => ({
      ...field,
      value: taskFieldValueHash[field.fieldId],
    }),
  );

  const handleChange = (_value, _row: FieldWithValue) => {
    onChange({ fieldId: _row.fieldId, fieldValue: _value });
  };

  return (
    <TaskFieldGroup
      groupedFields={groupBy(fieldsWithValue, 'fieldGroup')}
      onChange={handleChange}
      externalRefKey={externalRefKey}
      effort={effort}
      onExternalRefkeyChange={onExternalRefkeyChange}
      onEffortChange={onEffortChange}
      effortField={effortField}
    />
  );
};
